import React, { useRef, useEffect, useState } from "react"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import Chip from "./Chip"
import "./ChipsScroller.scss"
import { ChipsScrollerProps } from "../../types"

const ChipsScroller: React.FC<ChipsScrollerProps> = ({ chips, selectedChip, onChipClick, size = "small", textCase = "upper" }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const leftArrowRef = useRef<HTMLButtonElement>(null)
  const rightArrowRef = useRef<HTMLButtonElement>(null)
  const selectedChipRef = useRef<HTMLDivElement>(null)
  const [selectedChipWidth, setSelectedChipWidth] = useState(0)

  const updateScrollButtons = () => {
    if (scrollRef.current && leftArrowRef.current && rightArrowRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
      const showLeft = scrollLeft > 0
      const showRight = scrollLeft + clientWidth < scrollWidth

      leftArrowRef.current.style.display = showLeft ? "flex" : "none"
      rightArrowRef.current.style.display = showRight ? "flex" : "none"

      if (showLeft) {
        leftArrowRef.current.style.left = `${selectedChipWidth + 8}px`
      }
    }
  }

  useEffect(() => {
    updateScrollButtons()
    const handleScroll = () => updateScrollButtons()
    const scrollEl = scrollRef.current

    if (scrollEl) {
      scrollEl.addEventListener("scroll", handleScroll)
      window.addEventListener("resize", handleScroll)
    }

    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener("scroll", handleScroll)
        window.removeEventListener("resize", handleScroll)
      }
    }
  }, [chips, selectedChipWidth])

  useEffect(() => {
    if (selectedChipRef.current) {
      setSelectedChipWidth(selectedChipRef.current.offsetWidth)
    }
  }, [selectedChip])

  const handleLeftArrowClick = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: "smooth" })
    }
  }

  const handleRightArrowClick = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: "smooth" })
    }
  }

  return (
    <div className={`chips-scroller ${size}`}>
      <div className="chips-inner">
        <div ref={selectedChipRef} className="selected-chip-wrapper">
          <Chip
            {...selectedChip}
            size={size}
            onClick={() => onChipClick(selectedChip.id)}
            onCloseClick={
              selectedChip.showClose
                ? (e) => {
                    e.stopPropagation()
                    onChipClick("all")
                  }
                : undefined
            }
            textCase={textCase}
            isSelected={true}
          />
        </div>

        <div className="chips-scroll-container">
          <button ref={leftArrowRef} className="scroll-arrow left" onClick={handleLeftArrowClick}>
            <div className="arrow-circle">
              <ChevronLeftIcon />
            </div>
          </button>

          <div className="chips-scroll" ref={scrollRef} style={{ paddingLeft: selectedChipWidth + 8 }}>
            {chips.map((chip) => (
              <Chip key={chip.id} {...chip} size={size} onClick={() => onChipClick(chip.id)} isSelected={false} textCase={textCase} />
            ))}
          </div>

          <button ref={rightArrowRef} className="scroll-arrow right" onClick={handleRightArrowClick}>
            <div className="arrow-circle">
              <ChevronRightIcon />
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChipsScroller
